import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useTheme } from '../context/ThemeContext';
import { 
  Card, 
  CardContent, 
  Button, 
  TextField, 
  Typography, 
  Box, 
  Alert, 
  Chip,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import { AppIconButton } from '../components/ui';
import {
  Send as SendIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';

interface LogEntry {
  id: string;
  timestamp: Date;
  type: 'sent' | 'received' | 'error' | 'info';
  eventName: string;
  message: string;
  data?: any;
}

interface TestEvent {
  name: string;
  event: string;
  data: any;
}

const SocketDiagnostics: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const socket = useSocket();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [eventName, setEventName] = useState('notification');
  const [eventData, setEventData] = useState('');
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('disconnected');
  const logsEndRef = useRef<HTMLDivElement>(null);

  const testEvents: TestEvent[] = [
    {
      name: 'New Order',
      event: 'notification',
      data: {
        event: 'NEW_ORDER',
        title: 'New Order #123',
        message: 'New order received from Table 5',
        data: {
          orderId: 123,
          orderNumber: 'ORD-123',
          tableNumber: 'Table 5',
          totalAmount: 250.00
        }
      }
    },
    {
      name: 'Bill Request',
      event: 'bill:requested',
      data: {
        type: 'TABLE',
        orderId: 6,
        tableId: 1,
        tableName: 'Table 1',
        requestedBy: 'Socket Diagnostics',
        timestamp: new Date().toISOString(),
        totalAmount: 150.00,
        customerName: 'Test Customer'
      }
    },
    {
      name: 'Order Status',
      event: 'order:statusChanged',
      data: {
        orderId: 123,
        status: 'READY',
        orderNumber: 'ORD-123',
        timestamp: new Date().toISOString()
      }
    },
    {
      name: 'KOT Update',
      event: 'kot:new',
      data: {
        kotId: 456,
        kotNumber: 'KOT-456',
        orderId: 123,
        orderNumber: 'ORD-123',
        status: 'PENDING',
        items: [
          { name: 'Test Item 1', quantity: 2 },
          { name: 'Test Item 2', quantity: 1 }
        ]
      }
    }
  ];

  useEffect(() => {
    // Set initial data
    setEventData(JSON.stringify(testEvents[0].data, null, 2));

    // Check Socket.IO connection status
    if (socket?.isConnected) {
      setConnectionStatus('connected');
    } else {
      setConnectionStatus('disconnected');
    }

    // Add initial log
    addLog('info', 'System', 'Socket.IO diagnostics initialized', null);
  }, [socket]);

  useEffect(() => {
    // Auto-scroll to bottom when new logs are added
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const addLog = (type: LogEntry['type'], eventName: string, message: string, data: any) => {
    const newLog: LogEntry = {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
      type,
      eventName,
      message,
      data
    };
    
    setLogs(prev => {
      const newLogs = [...prev, newLog];
      // Keep only last 100 logs
      if (newLogs.length > 100) {
        return newLogs.slice(-100);
      }
      return newLogs;
    });
  };

  const sendTestEvent = async () => {
    try {
      setLoading(true);
      addLog('info', eventName, `Sending Socket.IO test event: ${eventName}`, null);
      
      const token = localStorage.getItem('token');
      const vendorId = user?.id || 2;
      
      // Parse event data
      let parsedData;
      try {
        parsedData = JSON.parse(eventData);
      } catch (e) {
        addLog('error', eventName, 'Invalid JSON in event data', null);
        setLoading(false);
        return;
      }

      // Use the test API to send the event
      let url = `/api/test/socket/vendor/${vendorId}`;
      
      if (eventName === 'bill:requested') {
        url += '/bill-request';
      } else if (eventName === 'notification') {
        url += '/notification';
      } else if (eventName.startsWith('order:')) {
        url += `/order-event?eventType=${eventName}`;
      } else {
        url += `/custom-event?event=${eventName}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(parsedData)
      });

      const result = await response.json();
      
      if (result.success) {
        addLog('sent', eventName, 'Socket.IO event sent successfully', result.data);
      } else {
        addLog('error', eventName, 'Failed to send Socket.IO event', result);
      }
    } catch (error: any) {
      addLog('error', eventName, `Error sending Socket.IO event: ${error.message}`, error);
    } finally {
      setLoading(false);
    }
  };

  const loadTestEvent = (testEvent: TestEvent) => {
    setEventName(testEvent.event);
    setEventData(JSON.stringify(testEvent.data, null, 2));
    addLog('info', 'System', `Loaded test event: ${testEvent.name}`, null);
  };

  const clearLogs = () => {
    setLogs([]);
    addLog('info', 'System', 'Logs cleared', null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getLogIcon = (type: LogEntry['type']) => {
    switch (type) {
      case 'sent': return <CheckCircleIcon fontSize="small" color="success" />;
      case 'received': return <CheckCircleIcon fontSize="small" color="primary" />;
      case 'error': return <ErrorIcon fontSize="small" color="error" />;
      case 'info': return <InfoIcon fontSize="small" color="info" />;
    }
  };

  const getLogColor = (type: LogEntry['type']) => {
    switch (type) {
      case 'sent': return theme.success;
      case 'received': return theme.primary;
      case 'error': return theme.danger || '#DC2626';
      case 'info': return theme.primary || '#5B9BD5';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Typography variant="h5" className="mb-4 sm:mb-6 font-bold" style={{ color: theme.text }}>
        Socket.IO Diagnostics
      </Typography>

      {/* Connection Status */}
      <Card className="mb-4 sm:mb-6">
        <CardContent>
          <Box display="flex" alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography variant="subtitle1">Connection Status</Typography>
              <Chip
                label={connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
                color={connectionStatus === 'connected' ? 'success' : 'error'}
                size="small"
              />
            </Box>
            <Box textAlign={{ xs: 'left', sm: 'right' }}>
              <Typography variant="body2" color="textSecondary">
                Server: localhost:9092
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Vendor ID: {user?.id || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {/* Event Testing Panel */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" className="mb-3 sm:mb-4">Send Test Event</Typography>
              
              {/* Quick Test Events */}
              <Box className="mb-4">
                <Typography variant="subtitle2" color="textSecondary" className="mb-2">
                  Quick Test Events:
                </Typography>
                <Grid container spacing={1}>
                  {testEvents.map((test, idx) => (
                    <Grid item xs={6} key={idx}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        onClick={() => loadTestEvent(test)}
                        sx={{
                          backgroundColor: 'transparent !important',
                          color: `${theme.gray?.[700] || '#374151'} !important`,
                          border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
                          fontWeight: 500,
                          textTransform: 'none',
                          '&:hover': { backgroundColor: `${theme.gray?.[100] || '#f3f4f6'} !important` }
                        }}
                      >
                        {test.name}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Event Name */}
              <TextField
                fullWidth
                label="Event Name"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="e.g., notification, bill:requested"
                className="mb-3 sm:mb-4"
                size="small"
                InputLabelProps={{ shrink: true }}
              />

              {/* Event Data */}
              <TextField
                fullWidth
                multiline
                rows={8}
                label="Event Data (JSON)"
                value={eventData}
                onChange={(e) => setEventData(e.target.value)}
                placeholder='{"key": "value"}'
                className="mb-3 sm:mb-4"
                size="small"
                style={{ fontFamily: 'monospace' }}
                InputLabelProps={{ shrink: true }}
                sx={{ mt: 1.5 }}
              />

              {/* Send Button */}
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={sendTestEvent}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{
                  backgroundColor: `${theme.accent + '22'} !important`,
                  color: `${theme.accent} !important`,
                  border: `1px solid ${theme.accent} !important`,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none !important',
                  '&:hover': { backgroundColor: `${theme.accent + '44'} !important`, borderColor: `${theme.accent} !important`, boxShadow: 'none !important' }
                }}
              >
                {loading ? 'Sending...' : 'Send Test Event'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Logs Panel */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" className="mb-4">
                <Typography variant="subtitle1">Event Logs</Typography>
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={clearLogs}
                  sx={{
                    backgroundColor: `${theme.primary + '22'} !important`,
                    color: `${theme.primary} !important`,
                    border: `1px solid ${theme.primary} !important`,
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': { backgroundColor: `${theme.primary + '44'} !important`, borderColor: `${theme.primary} !important` }
                  }}
                >
                  Clear
                </Button>
              </Box>

              <Paper 
                variant="outlined" 
                className="p-2"
                style={{ 
                  maxHeight: '45vh', 
                  overflowY: 'auto',
                  backgroundColor: theme.components.card.background + '33'
                }}
              >
                {logs.length === 0 ? (
                  <Box className="text-center py-8">
                    <Typography variant="body2" color="textSecondary">
                      No logs yet. Send a test event to see logs here.
                    </Typography>
                  </Box>
                ) : (
                  logs.map((log) => (
                    <Box
                      key={log.id}
                      className="mb-2 p-2 rounded"
                      style={{
                        backgroundColor: theme.components.card.background,
                        borderLeft: `4px solid ${getLogColor(log.type)}`
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        {getLogIcon(log.type)}
                        <Typography variant="subtitle2" fontWeight="bold">
                          {log.eventName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {log.timestamp.toLocaleTimeString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" className="mt-1">
                        {log.message}
                      </Typography>
                      {log.data && (
                        <Box className="mt-2">
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption" color="textSecondary">
                              Data:
                            </Typography>
                            <AppIconButton
                              icon={<CopyIcon fontSize="small" />}
                              tooltip="Copy data"
                              variant="ghost"
                              size="small"
                              onClick={() => copyToClipboard(JSON.stringify(log.data, null, 2))}
                            />
                          </Box>
                          <pre style={{ 
                            fontSize: '11px',
                            overflow: 'auto',
                            backgroundColor: theme.components.card.background + '66',
                            padding: '8px',
                            borderRadius: '4px'
                          }}>
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        </Box>
                      )}
                    </Box>
                  ))
                )}
                <div ref={logsEndRef} />
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Instructions */}
      <Card className="mt-6">
        <CardContent>
          <Typography variant="h6" className="mb-3">How to Use</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight="bold" className="mb-1">
                1. Quick Test
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Click any of the "Quick Test Events" buttons to load pre-configured events
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight="bold" className="mb-1">
                2. Custom Events
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Enter your own event name and JSON data for custom testing
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight="bold" className="mb-1">
                3. Monitor Results
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Check the logs panel and your notification bell for events
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </div>
  );
};

export default SocketDiagnostics;